import { useEffect, useRef, useState } from 'react'
import { Button, Icon, LoadingState, toastError, toastSuccess } from 'admination-react'
import { organisationInformationSheet, uploadOrganisationInformationSheet } from '@/core/api/organisation'
import { DropCard } from '@/core/components/modal/DropCard'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'

export function InformationsblattSection({ onSaved }: { onSaved?: () => void }) {
  const [present, setPresent] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [reading, setReading] = useState(true)
  const [attempt, setAttempt] = useState(0)
  const writing = useRef(false)

  useEffect(() => {
    let cancelled = false
    setReading(true)
    void organisationInformationSheet().then((result) => {
      if (!cancelled) {
        setPresent(result.vorhanden)
        setError(null)
      }
    }).catch((cause: unknown) => {
      if (!cancelled) setError(friendlyErrorMessage(cause, 'Das Informationsblatt konnte nicht geprüft werden. Bitte versuchen Sie es erneut.'))
    }).finally(() => {
      if (!cancelled) setReading(false)
    })
    return () => { cancelled = true }
  }, [attempt])

  async function upload(file: File) {
    if (writing.current) return
    const identity = captureWriteIdentity()
    writing.current = true
    setBusy(true)
    try {
      await uploadOrganisationInformationSheet(file)
      if (!isWriteIdentityCurrent(identity)) return
      setPresent(true)
      setFileName(file.name)
      setError(null)
      toastSuccess('Informationsblatt gespeichert.')
      onSaved?.()
    } catch (cause) {
      if (isWriteIdentityCurrent(identity)) toastError(friendlyErrorMessage(cause, 'Das Informationsblatt konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.'))
    } finally {
      writing.current = false
      if (isWriteIdentityCurrent(identity)) setBusy(false)
    }
  }

  return <div className="section">
    <h3 className="section-header">Informationsblatt</h3>
    <div className="section-body">
      {present === null && !error && <LoadingState>Informationsblatt wird geprüft …</LoadingState>}
      {error && <>
        <p className="field-error" role="alert">{error}</p>
        <Button loading={reading} onClick={() => { setReading(true); setAttempt((value) => value + 1) }}><Icon name="i-arrow-right" />Erneut prüfen</Button>
      </>}
      {present && <p className="section-note">Informationsblatt ist hinterlegt.</p>}
      <DropCard
        title="Informationsblatt als PDF auswählen oder hierher ziehen"
        body={fileName ?? (present ? 'Laden Sie eine PDF hoch, um das Informationsblatt zu ersetzen.' : 'Laden Sie das Informationsblatt Ihrer Organisation als PDF hoch.')}
        accept={['application/pdf', '.pdf']}
        loading={busy}
        disabled={reading}
        loadingLabel="Informationsblatt wird gespeichert …"
        onFile={(file) => void upload(file)}
      />
    </div>
  </div>
}
